"use client";
import React, { useEffect, useRef, useState } from "react";
import SectionHeader from "./section-header";
import { whyBrimmHQ } from "@/lib/data";
import Image from "next/image";

const Why = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = { threshold: 0.2 };

    const headerObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsHeaderVisible(true);
    }, observerOptions);

    const gridObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsGridVisible(true);
        setTimeout(() => setHasLoaded(true), 1500); // Allows the cinematic stagger load to finish, then strips the heavy CSS rules so hovers are snappy
      }
    }, observerOptions);

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (gridRef.current) gridObserver.observe(gridRef.current);

    return () => {
      headerObserver.disconnect();
      gridObserver.disconnect();
    };
  }, []);

  return (
    // figure this out....
    <section className="2xl:container 2xl:mx-auto 2xl:px-4 px-4 text-surface-black">
      <div className="space-y-6 lg:space-y-8 xl:space-y-12">
        <div
          ref={headerRef}
          className=" md:max-w-108 xl:max-w-143  grid place-self-center space-y-4 sm:space-y-5 lg:space-y-7 xl:space-y-12"
        >
          <SectionHeader title="WHY BRIMM HQ" />
          <div className="space-y-2.5 lg:space-y-4 xl:space-y-6 text-center">
            <div className="overflow-hidden">
              <h2
                className={`font-semibold leading-7.5 sm:leading-9 lg:leading-11  xl:leading-14 text-[21px] sm:text-[28px] lg:text-[37px] xl:text-5xl transition-transform duration-[1200ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  isHeaderVisible ? "translate-y-0" : "translate-y-[120%]"
                }`}
              >
                Why Nigerian Mothers Trust
                <span className="text-primary block md:inline italic pl-1.5">
                  BRIMM HQ
                </span>
              </h2>
            </div>
            <p
              className={`leading-[1.6] font-medium sm:leading-normal text-sm xl:text-base transition-all duration-[1500ms] delay-[300ms] ease-out ${
                isHeaderVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
            >
              Built with care- every feature is designed to deliver reliable,
              affordable, and dignified healthcare across Africa.
            </p>
          </div>
        </div>

        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6 px-0 md:px-8 lg:px-14 xl:px-24 text-base md:text-lg lg:text-xl xl:text-2xl"
        >
          {whyBrimmHQ.map((data, idx) => (
            <div
              key={data.title}
              className={`group gradient-fade rounded-[20px] drop-shadow-[0_0_28px_rgba(226,226,226,0.7)] outline outline-surface-light/88 py-5.5 xl:py-7.5 px-4 lg:px-6 xl:px-8 space-y-4 xl:space-y-6 bg-[linear-gradient(209.23deg,rgba(0,82,204,0.225)_0%,rgba(255,255,255,0.9)_13.87%)] transform transition-all ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-2 hover:drop-shadow-[0_20px_40px_rgba(0,0,0,0.08)] ${
                hasLoaded
                  ? "duration-150 hover:duration-300"
                  : "duration-[800ms]"
              } ${
                isGridVisible
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 translate-y-8 scale-95"
              }`}
              style={{
                transitionDelay:
                  !hasLoaded && isGridVisible ? `${idx * 150}ms` : "0ms",
              }}
            >
              <div className="p-2.25 xl:p-3.25 rounded-[10px] bg-primary/10 drop-shadow-[0_0_24px_rgba(226,226,226,0.7)] w-fit transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-3">
                <div className="relative size-5 xl:size-6">
                  <Image
                    src={data.icon}
                    alt=""
                    fill
                    quality={100}
                    className="object-center object-cover"
                  />
                </div>
              </div>
              <div className="space-y-2.5 xl:space-y-4 md:min-h-30">
                <p className=" font-semibold leading-6 ">{data.title}</p>
                <span className="text-sm xl:text-base leading-none lg:leading-4 xl:leading-6">
                  {data.description}
                </span>
              </div>

              <div className="flex items-center gap-x-2 p-2.5 xl:p-3 bg-primary/10 transition-colors duration-300 group-hover:bg-primary/20 outline-2 outline-surface-light/70 shadow-[0px_0px_20px_0px_#E2E2E2B2] rounded-[50px] w-fit">
                <span className="text-sm sm:text-xs lg:text-sm xl:text-base leading-4 xl:leading-6 text-primary font-semibold">
                  {data.badge}
                </span>
                <span className="uppercase text-surface text-xs">
                  {data.badgeLabel}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Why;
